import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PageHeader,
  TextField,
  toast,
} from '@eon/ui';
import { hooks } from '@eon/api-client';
import { formatDateTime } from '@eon/utils';

interface ProfileForm {
  name: string;
  description: string;
  creditLimitKwh: number;
}

export function ProfilePage() {
  const { t } = useTranslation();
  const profile = hooks.useOmOrganizationProfile();
  const update = hooks.useUpdateOmOrganizationProfile();
  const { register, handleSubmit, reset, formState } = useForm<ProfileForm>({
    defaultValues: { name: '', description: '', creditLimitKwh: 0 },
  });

  useEffect(() => {
    if (profile.data) {
      reset({
        name: profile.data.name ?? '',
        description: profile.data.description ?? '',
        creditLimitKwh: profile.data.creditLimitKwh ?? 0,
      });
    }
  }, [profile.data, reset]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      const credit = Number(values.creditLimitKwh);
      await update.mutateAsync({
        name: values.name.trim() || undefined,
        description: values.description.trim() || undefined,
        creditLimitKwh: Number.isFinite(credit) && credit >= 0 ? credit : 0,
      });
      toast.success(t('portal.profile.updated'));
      void profile.refetch();
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  });

  const org = profile.data;

  return (
    <>
      <PageHeader title={t('nav.orgProfile')} description={t('portal.profile.description')} />
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>{t('portal.profile.orgTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {profile.isLoading ? (
            <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>{t('common.id')}</Label>
                <Input value={org?.id ?? ''} disabled className="font-mono text-xs" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="org-name">{t('portal.profile.name')}</Label>
                <Input id="org-name" {...register('name', { required: true })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="org-desc">{t('portal.profile.descriptionField')}</Label>
                <TextField id="org-desc" {...register('description')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="org-credit">{t('portal.profile.creditLimit')}</Label>
                <Input
                  id="org-credit"
                  type="number"
                  min={0}
                  step={1}
                  {...register('creditLimitKwh', { valueAsNumber: true, min: 0 })}
                />
                <p className="text-muted-foreground text-xs">{t('portal.profile.creditLimitHint')}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{t('portal.profile.status')}</Label>
                  <div>
                    <Badge variant={org?.active !== false ? 'success' : 'secondary'}>
                      {org?.active !== false ? t('portal.profile.active') : t('portal.profile.inactive')}
                    </Badge>
                  </div>
                </div>
                {org?.createdAt ? (
                  <div className="space-y-1.5">
                    <Label>{t('user.createdAt')}</Label>
                    <p className="text-sm">{formatDateTime(org.createdAt)}</p>
                  </div>
                ) : null}
                {org?.updatedAt ? (
                  <div className="space-y-1.5">
                    <Label>{t('user.updatedAt')}</Label>
                    <p className="text-sm">{formatDateTime(org.updatedAt)}</p>
                  </div>
                ) : null}
              </div>
              <Button type="submit" disabled={formState.isSubmitting || update.isPending}>
                {t('actions.save')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </>
  );
}
